import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { useTranslation } from "@/packages/utils";
import type { TimeRange, KpiId, KpiData } from "./dashboard.data";
import { KPI_MOCK } from "./dashboard.data";

// ── KPI Display Config ───────────────────────────────────────────

const KPI_LABEL_KEYS: Record<KpiId, string> = {
  activeAudits: "dashboard:kpi.activeAudits",
  completedAudits: "dashboard:kpi.completed",
  openFindings: "dashboard:kpi.openFindings",
  missingEvidence: "dashboard:kpi.missingEvidence",
  fixRate: "dashboard:kpi.fixRate",
  upcomingDeadlines: "dashboard:kpi.upcomingDeadlines",
};

const KPI_TREND_LABEL_KEYS: Record<KpiId, string> = {
  activeAudits: "dashboard:kpiTrend.activeAudits",
  completedAudits: "dashboard:kpiTrend.completed",
  openFindings: "dashboard:kpiTrend.openFindings",
  missingEvidence: "dashboard:kpiTrend.missingEvidence",
  fixRate: "dashboard:kpiTrend.fixRate",
  upcomingDeadlines: "dashboard:kpiTrend.upcomingDeadlines",
};

// ── Trend Arrow Icon ────────────────────────────────────────────

function TrendArrowIcon({
  direction,
  className,
}: {
  direction: "up" | "down";
  className?: string;
}) {
  return direction === "up" ? (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-label="Trend up"
    >
      <path d="M4 12 L12 4" />
      <path d="M7 4 L12 4 L12 9" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-label="Trend down"
    >
      <path d="M4 4 L12 12" />
      <path d="M7 12 L12 12 L12 7" />
    </svg>
  );
}

// ── TrendBadge ───────────────────────────────────────────────────

function TrendBadge({
  trend,
  invertTrend,
}: {
  trend: number;
  invertTrend?: boolean;
}) {
  if (trend === 0) return null;
  const isPositive = trend > 0;
  const isGood = invertTrend ? !isPositive : isPositive;
  const direction = isPositive ? "up" : "down";

  const colorClass = isGood
    ? "text-emerald-400 bg-emerald-400/10"
    : "text-red-400 bg-red-400/10";

  const sign = isPositive ? "+" : "";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colorClass}`}
    >
      <TrendArrowIcon direction={direction} className="size-3" />
      {sign}
      {trend}%
    </span>
  );
}

// ── TrendLabel ──────────────────────────────────────────────────

function TrendLabel({
  trend,
  invertTrend,
  label,
}: {
  trend: number;
  invertTrend?: boolean;
  label: string;
}) {
  if (trend === 0) return null;
  const isPositive = trend > 0;
  const isGood = invertTrend ? !isPositive : isPositive;
  const direction = isPositive ? "up" : "down";

  const colorClass = isGood ? "text-emerald-400" : "text-red-400";

  return (
    <div
      className={`flex items-center gap-1.5 text-sm font-semibold ${colorClass}`}
    >
      <span>{label}</span>
      <TrendArrowIcon direction={direction} className="size-3.5" />
    </div>
  );
}

// ── Grid Helpers ─────────────────────────────────────────────────

const GRID_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

// ── SortableKpiCard ──────────────────────────────────────────────

interface SortableKpiCardProps {
  kpi: KpiData;
  children: React.ReactNode;
}

function SortableKpiCard({ kpi, children }: SortableKpiCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: kpi.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-xl border border-border bg-surface p-3 flex flex-col gap-2 ${
        isDragging ? "opacity-50 ring-2 ring-accent/30" : ""
      }`}
    >
      {/* Drag handle — desktop only (hidden on touch via CSS) */}
      <button
        type="button"
        className="drag-handle absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-muted hover:text-secondary touch-none"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} />
      </button>

      {children}
    </div>
  );
}

// ── KpiCards ─────────────────────────────────────────────────────

interface KpiCardsProps {
  timeRange: TimeRange;
  visibleKpis: KpiId[];
  kpiOrder: KpiId[];
  onKpiOrderChange: (newOrder: KpiId[]) => void;
}

export function KpiCards({
  timeRange,
  visibleKpis,
  kpiOrder,
  onKpiOrderChange,
}: KpiCardsProps) {
  const { t } = useTranslation(["dashboard"]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const kpiData = KPI_MOCK[timeRange];
  const visibleSet = new Set(visibleKpis);

  // Ordered visible KPIs for display
  const filteredKpis: KpiData[] = kpiOrder
    .filter((id) => visibleSet.has(id))
    .map((id) => kpiData.find((kpi) => kpi.id === id) as KpiData);

  const count = filteredKpis.length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Reorder within the full kpiOrder array (not just visible subset)
    const oldIndex = kpiOrder.indexOf(active.id as KpiId);
    const newIndex = kpiOrder.indexOf(over.id as KpiId);
    onKpiOrderChange(arrayMove(kpiOrder, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={filteredKpis.map((k) => k.id)}
        strategy={horizontalListSortingStrategy}
      >
        <div className={`grid ${GRID_CLASS[count] ?? "grid-cols-3"} gap-3`}>
          {filteredKpis.map((kpi) => (
            <SortableKpiCard key={kpi.id} kpi={kpi}>
              {/* Top row: title + badge */}
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-muted leading-tight pl-3">
                  {
                    t(
                      KPI_LABEL_KEYS[kpi.id] as "dashboard:kpi.activeAudits",
                    ) as string
                  }
                </p>
                {kpi.trend != null && (
                  <TrendBadge trend={kpi.trend} invertTrend={kpi.invertTrend} />
                )}
              </div>

              {/* Large value */}
              <div className="flex items-baseline gap-1">
                {kpi.unit === "Tage" ? (
                  <span className="text-2xl font-bold text-primary tabular-nums">
                    {kpi.value}
                  </span>
                ) : (
                  <AnimatedNumber
                    value={kpi.value}
                    className="text-2xl font-bold text-primary tabular-nums"
                  />
                )}
                {kpi.unit && (
                  <span className="text-sm font-medium text-secondary">
                    {kpi.unit}
                  </span>
                )}
              </div>

              {/* Trend label */}
              {kpi.trend != null && kpi.trend !== 0 && (
                <TrendLabel
                  trend={kpi.trend}
                  invertTrend={kpi.invertTrend}
                  label={
                    t(
                      KPI_TREND_LABEL_KEYS[
                        kpi.id
                      ] as "dashboard:kpiTrend.activeAudits",
                    ) as string
                  }
                />
              )}
            </SortableKpiCard>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
